import { useState, useEffect } from 'react';
import { X, Zap, Battery, Clock, Calendar, TrendingUp, CircleCheck, Info, AlertCircle, RefreshCw, MapPin, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { ChargingSummary, Transaction, Tariff } from '../../types';
import { transactionService } from '../../services/transactionService';
import { toast } from 'sonner';
import { BalanceIcon } from './icons/BalanceIcon';
import { formatBalance } from '../../utils/balanceFormatter';
import { mapTariffToDisplay } from '../../utils/tariff';

interface ChargingSummaryPageProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionId?: string;
  transaction?: Transaction | null;
  summaryData?: ChargingSummary | null;
}

interface DisplayData {
  id: string;
  energyUsed: number;
  amount: number;
  duration: number;
  stopReason: string;
  meterStart: number;
  meterEnd: number;
  finalBatteryLevel: number;
  lastRecordedPower: number;
  sessionStart: string;
  sessionEnd: string;
  tariffs: Tariff[];
  locationName?: string;
  chargePointName?: string;
  address?: string;
}

/**
 * Calculate duration in minutes between two dates
 */
function calculateDuration(startDate: string | Date | undefined, endDate: string | Date | undefined): number {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
}

/**
 * Format date to locale string
 */
function formatDate(date: string | Date | undefined): string {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Transform backend transaction to display format
 */
function transformTransactionToDisplay(transaction: any): DisplayData {
  // Use startConfirmedByEvseAt (actual charging start) instead of createdAt (request time)
  const sessionStartTime = transaction.startConfirmedByEvseAt || transaction.createdAt;
  const duration = calculateDuration(sessionStartTime, transaction.stoppedAt);
  const tariffs = mapTariffToDisplay(transaction.tariff);

  return {
    id: transaction.id,
    energyUsed: transaction.totalKwh || 0,
    amount: transaction.totalCost || 0,
    duration,
    stopReason: transaction.stoppedReason || 'Completed',
    meterStart: transaction.meterStart || 0,
    meterEnd: transaction.meterEnd || transaction.totalKwh || 0,
    finalBatteryLevel: transaction.currentSoc || 0,
    lastRecordedPower: transaction.currentPowerKw || 0,
    sessionStart: formatDate(sessionStartTime),
    sessionEnd: formatDate(transaction.stoppedAt),
    tariffs,
    locationName: transaction.chargePoint?.location?.name,
    chargePointName: transaction.chargePoint?.chargerName,
    address: transaction.chargePoint?.location?.address,
  };
}

/**
 * Loading skeleton component
 */
function LoadingSkeleton() {
  return (
    <div className="animate-pulse">
      {/* Success Badge Skeleton */}
      <div className="flex items-center justify-center mb-6">
        <div className="h-10 w-48 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
      </div>

      {/* Amount Card Skeleton */}
      <div className="bg-gray-200 dark:bg-gray-700 rounded-3xl h-36 mb-6"></div>

      {/* Quick Stats Grid Skeleton */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-200 dark:bg-gray-700 rounded-2xl h-24"></div>
        <div className="bg-gray-200 dark:bg-gray-700 rounded-2xl h-24"></div>
      </div>

      {/* Battery & Power Status Skeleton */}
      <div className="bg-gray-200 dark:bg-gray-700 rounded-2xl h-28 mb-6"></div>

      {/* Meter Readings Skeleton */}
      <div className="bg-gray-200 dark:bg-gray-700 rounded-2xl h-36 mb-6"></div>

      {/* Session Timeline Skeleton */}
      <div className="bg-gray-200 dark:bg-gray-700 rounded-2xl h-44 mb-6"></div>

      {/* Button Skeleton */}
      <div className="bg-gray-200 dark:bg-gray-700 rounded-2xl h-14"></div>
    </div>
  );
}

export function ChargingSummaryPage({ open, onOpenChange, transactionId, transaction, summaryData }: ChargingSummaryPageProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchedData, setFetchedData] = useState<DisplayData | null>(null);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [isPricingPending, setIsPricingPending] = useState(false);

  // Fetch transaction data when modal opens and transactionId is provided
  useEffect(() => {
    if (!open) {
      // Reset state when modal closes
      setFetchedData(null);
      setError(null);
      setIsPricingPending(false);
      return;
    }

    // If we already have summaryData (from stop charging flow), use it directly
    if (summaryData) {
      setFetchedData(null);
      setError(null);
      return;
    }

    // If transactionId is provided and no summaryData, fetch from backend
    if (transactionId && !summaryData) {
      fetchTransactionData(transactionId);
    }
  }, [open, transactionId, summaryData]);

  // Poll for pricing when amount is 0 (pricing calculated async after stop)
  useEffect(() => {
    if (!open || !transactionId || !isPricingPending) return;

    let attempts = 0;
    const maxAttempts = 10;
    const intervalMs = 3000;

    const pollInterval = setInterval(async () => {
      attempts++;
      console.log(`[ChargingSummaryPage] Polling for pricing (attempt ${attempts}/${maxAttempts})`);

      try {
        const txn = await transactionService.getTransactionById(transactionId);
        const displayData = transformTransactionToDisplay(txn);

        if (displayData.amount > 0) {
          console.log('[ChargingSummaryPage] Pricing available:', displayData.amount);
          setFetchedData(displayData);
          setIsPricingPending(false);
          clearInterval(pollInterval);
        } else if (attempts >= maxAttempts) {
          console.log('[ChargingSummaryPage] Pricing poll timed out');
          setIsPricingPending(false);
          clearInterval(pollInterval);
        }
      } catch (err) {
        console.error('[ChargingSummaryPage] Pricing poll error:', err);
        if (attempts >= maxAttempts) {
          setIsPricingPending(false);
          clearInterval(pollInterval);
        }
      }
    }, intervalMs);

    return () => clearInterval(pollInterval);
  }, [open, transactionId, isPricingPending]);

  const fetchTransactionData = async (txnId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const txn = await transactionService.getTransactionById(txnId);
      const displayData = transformTransactionToDisplay(txn);
      setFetchedData(displayData);

      // If amount is 0, pricing hasn't been calculated yet - start polling
      if (displayData.amount === 0) {
        console.log('[ChargingSummaryPage] Amount is 0, pricing pending - will poll');
        setIsPricingPending(true);
      }
    } catch (err: any) {
      console.error('[ChargingSummaryPage] Failed to fetch transaction:', err);
      const errorMessage = err.response?.status === 404
        ? 'Transaction not found'
        : err.response?.data?.message || 'Failed to load transaction details';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    if (transactionId) {
      fetchTransactionData(transactionId);
    }
  };

  if (!open) return null;

  const onClose = () => onOpenChange(false);

  // Determine which data source to use
  // Priority: summaryData (from stop charging) > fetchedData (from API) > transaction prop
  let txn: DisplayData | null = null;

  if (summaryData) {
    txn = summaryData as DisplayData;
  } else if (fetchedData) {
    txn = fetchedData;
  } else if (transaction) {
    // Use startConfirmedByEvseAt (actual charging start) instead of createdAt (request time)
    const txnSessionStart = transaction.startConfirmedByEvseAt || transaction.createdAt || transaction.startDate;
    txn = {
      id: transaction.id,
      energyUsed: transaction.totalKwh || transaction.energyUsed || 0,
      amount: transaction.totalCost || transaction.amount || 0,
      duration: calculateDuration(txnSessionStart, transaction.stoppedAt),
      stopReason: 'Completed',
      meterStart: 0,
      meterEnd: transaction.totalKwh || 0,
      finalBatteryLevel: transaction.currentSoc || transaction.currentBattery || 0,
      lastRecordedPower: transaction.currentPowerKw || 0,
      sessionStart: formatDate(txnSessionStart),
      sessionEnd: formatDate(transaction.stoppedAt || transaction.endDate),
      tariffs: [],
      locationName: transaction.chargePoint?.location?.name,
      chargePointName: transaction.chargePoint?.name,
    };
  }

  // If no data and not loading, this is an invalid state
  const hasNoData = !txn && !isLoading && !error;

  const formatDuration = (mins: number) => {
    if (!mins || mins === 0) return '0m';
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };


  return (
    <div className="fixed inset-0 bg-white dark:bg-gray-900 z-[2000] overflow-auto transition-colors">
      {/* Header — `fixed inset-0` escapes ProtectedLayout's safe-area padding,
          so the top inset is applied here; otherwise the header renders under
          the notch / Dynamic Island on iOS. */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 pb-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)]">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Charging Summary</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {isLoading ? 'Loading...' : txn ? `Transaction #${txn.id.slice(0, 8)}...` : 'No transaction'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 pb-24">
        {/* Loading State */}
        {isLoading && <LoadingSkeleton />}

        {/* Error State */}
        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Unable to Load Transaction
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-center mb-6 max-w-xs">
              {error}
            </p>
            <Button
              onClick={handleRetry}
              className="flex items-center gap-2"
              variant="outline"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </Button>
          </div>
        )}

        {/* No Data State - Invalid component usage */}
        {hasNoData && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Transaction Data
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-center mb-6 max-w-xs">
              Unable to display transaction details.
            </p>
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        )}

        {/* Data Display */}
        {!isLoading && !error && txn && (
          <>
            {/* Success Badge */}
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-full px-6 py-3">
                <CircleCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
                <span className="text-green-700 dark:text-green-300 font-semibold">Session Completed</span>
              </div>
            </div>

            {/* Location Info (if available) */}
            {(txn.locationName || txn.chargePointName) && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4 mb-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#8bc43e] mt-0.5 flex-shrink-0" />
                  <div>
                    {txn.locationName && (
                      <p className="font-semibold text-gray-900 dark:text-white">{txn.locationName}</p>
                    )}
                    {txn.chargePointName && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">{txn.chargePointName}</p>
                    )}
                    {txn.address && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{txn.address}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Amount Card - Most Important */}
            <div className="bg-gradient-to-br from-[#8bc43e] to-[#6fa02e] rounded-3xl p-6 mb-6 shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/80 text-sm font-medium">Total Cost</span>
                <BalanceIcon className="w-5 h-5 text-white/60" />
              </div>
              <div className="text-4xl font-bold text-white mb-1 flex items-center gap-2">
                <BalanceIcon className="w-8 h-8" />
                {isPricingPending ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-7 h-7 animate-spin" />
                    <span className="text-2xl">Calculating...</span>
                  </span>
                ) : (
                  formatBalance(txn.amount)
                )}
              </div>
              <div className="flex items-center gap-2 text-white/90 text-sm">
                <Zap className="w-4 h-4" />
                <span>{(txn.energyUsed || 0).toFixed(2)} kWh charged</span>
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Duration */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Duration</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatDuration(txn.duration)}</p>
              </div>

              {/* Energy */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Energy Used</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{(txn.energyUsed || 0).toFixed(2)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">kWh</p>
              </div>
            </div>

            {/* Battery & Power Status */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Battery className="w-4 h-4 text-[#8bc43e]" />
                Final Status
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Battery Level</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                      {txn.finalBatteryLevel ? `${txn.finalBatteryLevel}%` : 'N/A'}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Last Charging Speed</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                      {txn.lastRecordedPower ? (txn.lastRecordedPower).toFixed(2) : '0.00'}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">kW</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Meter Readings */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Info className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                Meter Readings
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Meter Start</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {txn.meterStart ? `${txn.meterStart.toFixed(1)} kWh` : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Meter End</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {txn.meterEnd ? `${txn.meterEnd.toFixed(1)} kWh` : 'N/A'}
                  </span>
                </div>
                <div className="h-px bg-gray-200 dark:bg-gray-700"></div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Consumed</span>
                  <span className="text-sm font-bold text-[#8bc43e]">{(txn.energyUsed || 0).toFixed(2)} kWh</span>
                </div>
              </div>
            </div>

            {/* Tariff Information (only show if tariffs exist) */}
            {txn.tariffs && txn.tariffs.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 mb-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Tariff Details</h3>
                <div className="space-y-3">
                  {txn.tariffs.map((tariff, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{tariff.name}</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {tariff.rate} {tariff.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Session Timeline */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                Session Timeline
              </h3>

              {/* Timeline */}
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-[7px] top-6 bottom-6 w-0.5 bg-gray-200 dark:bg-gray-700"></div>

                {/* Session Started */}
                <div className="relative flex gap-4 mb-8">
                  <div className="flex-shrink-0 w-4 h-4 rounded-full bg-[#8bc43e] border-4 border-green-100 dark:border-green-900/50 mt-1"></div>
                  <div className="flex-1 pt-0.5">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Session Started</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{txn.sessionStart}</p>
                  </div>
                </div>

                {/* Session Ended */}
                <div className="relative flex gap-4">
                  <div className="flex-shrink-0 w-4 h-4 rounded-full bg-gray-400 dark:bg-gray-600 border-4 border-gray-100 dark:border-gray-700 mt-1"></div>
                  <div className="flex-1 pt-0.5">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Session Ended</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{txn.sessionEnd}</p>
                    <div className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-full px-3 py-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-500 dark:bg-gray-400"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-300 font-medium">Reason: {txn.stopReason}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </>
        )}
      </div>
    </div>
  );
}
